'use client'

import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  Brain, 
  TrendingUp, 
  BarChart3,
  Sparkles,
  Zap,
  Database,
  AlertCircle,
  Crosshair,
  Contact
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useTheme } from 'next-themes'
import { useIsMobile } from '@/hooks/use-mobile'

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20, scale: 0.95 },
  visible: { 
    opacity: 1, 
    y: 0, 
    scale: 1,
    transition: {
      type: "spring",
      stiffness: 100,
      damping: 15
    }
  },
};

export function ProcessFlow() {
  const { resolvedTheme } = useTheme()
  const isDark = resolvedTheme === 'dark'
  const isMobile = useIsMobile()
  
  const projectManagementFeatures = [
    {
      icon: Crosshair,
      title: 'Capture Management',
      description: 'Manage your full capture lifecycle—from opportunity identification through proposal submission—with structured workflows, gate reviews, and win strategy tracking',
      gradient: 'from-blue-600 to-cyan-600',
    },
    {
      icon: Contact,
      title: 'Government Contracting CRM',
      description: 'Track agency contacts, teaming partners, and past performance in a CRM purpose-built for government contractors, with relationship mapping and engagement history',
      gradient: 'from-indigo-600 to-blue-700',
    },
    {
      icon: BarChart3,
      title: 'Bid Pipeline & Mapping',
      description: 'Visualize your entire bid pipeline with interactive maps, geographic insights, and stage-by-stage tracking from pursuit to award',
      gradient: 'from-rose-600 to-pink-700',
    },
    {
      icon: AlertCircle,
      title: 'Compliance Monitoring',
      description: 'Automated compliance checks and alerts to ensure regulatory requirements are met throughout the bid lifecycle',
      gradient: 'from-amber-600 to-orange-600',
    },
  ]
  
  return (
    <section className="py-24 overflow-hidden">
      <div className="container mx-auto px-4">
        {/* AI Matching Process - Enhanced */}
        <div className="max-w-6xl mx-auto mb-12 md:mb-24">
          <motion.div
            initial={{ opacity: 0, y: isMobile ? 20 : 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: isMobile ? 0.3 : 0.8 }}
            className={cn(
              "rounded-3xl p-6 md:p-12 relative overflow-hidden border",
              isMobile 
                ? "bg-slate-900/95 border-blue-500/30" 
                : "backdrop-blur-xl border-white/20 dark:border-white/10 bg-white/5 dark:bg-black/10"
            )}
            style={isMobile ? {
              boxShadow: '0 4px 20px rgba(59, 130, 246, 0.15)',
            } : {
              boxShadow: `
                0 8px 32px 0 rgba(31, 38, 135, 0.1),
                0 0 0 1px rgba(255, 255, 255, 0.1) inset,
                0 0 60px rgba(59, 130, 246, 0.1)
              `,
            }}
          >
            {/* Background Gradient Effect */}
            <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-purple-500/5 to-emerald-500/5" />
            
            <div className="relative z-10">
              {/* Header */}
              <div className="text-center mb-12">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-4">
                  <Brain className="w-4 h-4 text-primary" />
                  <span className="text-sm font-medium text-primary">Proprietary AI Formula</span>
                </div>
                <h2 className="text-3xl md:text-4xl font-bold mb-4 pb-1 leading-[1.15] bg-clip-text text-transparent bg-gradient-to-r from-sky-400 via-blue-500 to-blue-600">
                  Optimized Bid Matching & Real-Time Notifications
                </h2>
                <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                  Our proprietary AI formula continuously learns from millions of contract data points to 
                  surface your highest-value matches—and notifies you the moment new opportunities align 
                  with your profile so you never miss a deadline.
                </p>
              </div>

              {/* Enhanced Flow Diagram */}
              <div className="relative">
                {/* Flow Steps - Enhanced */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative z-10">
                  {/* Step 1: Data Collection & Analysis */}
                  <motion.div
                    initial={{ opacity: 0, y: 30, scale: 0.95 }}
                    whileInView={{ opacity: 1, y: 0, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.2, type: "spring", stiffness: 100 }}
                    className="text-center group"
                  >
                    <div className={cn(
                      "mb-6 inline-flex items-center justify-center w-20 h-20 rounded-2xl shadow-xl group-hover:shadow-2xl transition-all duration-300 group-hover:scale-110",
                      isDark 
                        ? "bg-gradient-to-br from-blue-600 to-cyan-600" 
                        : "bg-gradient-to-br from-blue-100 to-cyan-100"
                    )}>
                      <Database className={cn(
                        "w-10 h-10",
                        isDark ? "text-white" : "text-blue-600"
                      )} />
                    </div>
                    <h4 className="font-bold text-xl mb-3 text-foreground">Multi-Source Data Aggregation</h4>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      Our AI continuously monitors and aggregates contract opportunities from federal, 
                      state, and local sources, building a comprehensive database of millions of historical 
                      and active opportunities.
                    </p>
                  </motion.div>

                  {/* Step 2: Proprietary Formula */}
                  <motion.div
                    initial={{ opacity: 0, y: 30, scale: 0.95 }}
                    whileInView={{ opacity: 1, y: 0, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.4, type: "spring", stiffness: 100 }}
                    className="text-center group"
                  >
                    <div className={cn(
                      "mb-6 inline-flex items-center justify-center w-20 h-20 rounded-2xl shadow-xl group-hover:shadow-2xl transition-all duration-300 group-hover:scale-110",
                      isDark 
                        ? "bg-gradient-to-br from-indigo-600 to-blue-700" 
                        : "bg-gradient-to-br from-indigo-100 to-blue-100"
                    )}>
                      <Brain className={cn(
                        "w-10 h-10",
                        isDark ? "text-white" : "text-indigo-600"
                      )} />
                    </div>
                    <h4 className="font-bold text-xl mb-3 text-foreground">Proprietary Matching Formula</h4>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      Our advanced machine learning algorithms analyze NAICS codes, keywords, location data, 
                      past performance, and market trends using a proprietary formula that adapts and improves 
                      based on real-world win outcomes.
                    </p>
                  </motion.div>

                  {/* Step 3: Optimized Matching */}
                  <motion.div
                    initial={{ opacity: 0, y: 30, scale: 0.95 }}
                    whileInView={{ opacity: 1, y: 0, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.6, type: "spring", stiffness: 100 }}
                    className="text-center group"
                  >
                    <div className={cn(
                      "mb-6 inline-flex items-center justify-center w-20 h-20 rounded-2xl shadow-xl group-hover:shadow-2xl transition-all duration-300 group-hover:scale-110",
                      isDark 
                        ? "bg-gradient-to-br from-blue-700 to-indigo-700" 
                        : "bg-gradient-to-br from-blue-100 to-indigo-100"
                    )}>
                      <TrendingUp className={cn(
                        "w-10 h-10",
                        isDark ? "text-white" : "text-blue-700"
                      )} />
                    </div>
                    <h4 className="font-bold text-xl mb-3 text-foreground">Optimized Bid Matching</h4>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      Each opportunity receives a precision-matched score (0-100) that predicts your 
                      likelihood of success, helping you focus resources on the highest-value opportunities 
                      with the greatest win probability.
                    </p>
                  </motion.div>
                </div>

                {/* Additional Info Box */}
                <motion.div
                  initial={{ opacity: 0, y: isMobile ? 10 : 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: isMobile ? 0.2 : 0.8 }}
                  className={cn(
                    "mt-8 md:mt-12 p-4 md:p-6 rounded-2xl border",
                    isMobile 
                      ? "bg-slate-800/90 border-slate-700/50" 
                      : "backdrop-blur-xl border-white/20 dark:border-white/10 bg-white/5 dark:bg-black/10"
                  )}
                  style={isMobile ? {
                    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.2)',
                  } : {
                    boxShadow: `0 4px 16px 0 rgba(31, 38, 135, 0.08), 0 0 0 1px rgba(255, 255, 255, 0.08) inset`,
                  }}
                >
                  <div className="flex items-start gap-4">
                    <div className="p-2 rounded-lg bg-primary/20">
                      <Sparkles className="w-6 h-6 text-primary" />
                    </div>
                    <div className="flex-1">
                      <h5 className="font-bold text-lg mb-2 text-foreground">Continuous Learning & Improvement</h5>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        Our proprietary formula continuously evolves, learning from every bid outcome to refine 
                        matching accuracy. The more you use the platform, the smarter it becomes at identifying 
                        opportunities that align with your unique capabilities and strategic goals.
                      </p>
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Project Management Tools Section */}
        <motion.div
          initial={{ opacity: 0, y: isMobile ? 20 : 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: isMobile ? 0.3 : 0.8 }}
          className="max-w-6xl mx-auto mt-12 md:mt-24"
        >
          <div className={cn(
            "rounded-3xl p-6 md:p-12 relative overflow-hidden border",
            isMobile 
              ? "bg-slate-900/95 border-blue-500/30" 
              : "backdrop-blur-xl border-white/20 dark:border-white/10 bg-white/5 dark:bg-black/10"
          )}
            style={isMobile ? {
              boxShadow: '0 4px 20px rgba(59, 130, 246, 0.15)',
            } : {
              boxShadow: `
                0 8px 32px 0 rgba(31, 38, 135, 0.1),
                0 0 0 1px rgba(255, 255, 255, 0.1) inset,
                0 0 60px rgba(59, 130, 246, 0.1)
              `,
            }}
          >
            {!isMobile && (
              <div 
                className="absolute inset-0 rounded-3xl border-2 border-blue-400/30 dark:border-blue-500/20"
                style={{
                  animation: 'hero-glow-pulse 3s ease-in-out infinite',
                  boxShadow: '0 0 40px rgba(59, 130, 246, 0.2)',
                }}
              />
            )}
            
            <div className="relative z-10">
              <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-4">
              <Zap className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium text-primary">Capture Management</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-4 pb-1 leading-[1.15] bg-clip-text text-transparent bg-gradient-to-r from-sky-400 via-blue-500 to-blue-600">
              Capture Management & Government CRM
            </h2>
            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
              End-to-end capture management with a built-in government contracting CRM to track relationships,
              manage your pipeline, and drive opportunities from discovery to award
            </p>
          </div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            className="grid grid-cols-1 md:grid-cols-2 gap-6"
          >
            {projectManagementFeatures.map((feature, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                className="group relative"
              >
                <div className={cn(
                  "relative h-full rounded-2xl p-4 md:p-6 border",
                  "transition-all duration-300",
                  isMobile 
                    ? "bg-slate-800/90 border-slate-700/50" 
                    : "backdrop-blur-xl border-white/20 dark:border-white/10 bg-white/5 dark:bg-black/10 hover:shadow-2xl hover:scale-[1.02]"
                )}
                style={isMobile ? {
                  boxShadow: '0 2px 10px rgba(0, 0, 0, 0.2)',
                } : {
                  boxShadow: `0 4px 16px 0 rgba(31, 38, 135, 0.08), 0 0 0 1px rgba(255, 255, 255, 0.08) inset`,
                }}>
                  {/* Gradient Background */}
                  <div 
                    className={cn(
                      "absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-10 transition-opacity duration-300",
                      `bg-gradient-to-br ${feature.gradient}`
                    )}
                  />
                  
                  {/* Border Glow */}
                  <motion.div
                    className={cn(
                      "absolute -inset-0.5 rounded-2xl opacity-0 group-hover:opacity-50 blur-sm transition-opacity duration-300",
                      `bg-gradient-to-br ${feature.gradient}`
                    )}
                  />

                  <div className="relative z-10 flex items-start gap-4">
                    {/* Icon */}
                    <div className={cn(
                      "p-3 rounded-xl flex-shrink-0 transition-all duration-300 group-hover:scale-110",
                      isDark 
                        ? `bg-gradient-to-br ${feature.gradient}`
                        : feature.gradient.includes('blue-600 to-cyan')
                          ? 'bg-gradient-to-br from-blue-100 to-cyan-100'
                          : feature.gradient.includes('indigo-600')
                          ? 'bg-gradient-to-br from-indigo-100 to-blue-100'
                          : feature.gradient.includes('amber')
                          ? 'bg-gradient-to-br from-amber-100 to-orange-100'
                          : feature.gradient.includes('rose')
                          ? 'bg-gradient-to-br from-rose-100 to-pink-100'
                          : feature.gradient.includes('emerald')
                          ? 'bg-gradient-to-br from-emerald-100 to-teal-100'
                          : feature.gradient.includes('violet')
                          ? 'bg-gradient-to-br from-violet-100 to-purple-100'
                          : 'bg-gradient-to-br from-blue-100 to-cyan-100'
                    )}>
                      {(() => {
                        const Icon = feature.icon;
                        const iconColor = isDark 
                          ? 'text-white'
                          : feature.gradient.includes('blue-600 to-cyan')
                          ? 'text-blue-600'
                          : feature.gradient.includes('indigo-600')
                          ? 'text-indigo-600'
                          : feature.gradient.includes('amber')
                          ? 'text-amber-600'
                          : feature.gradient.includes('rose')
                          ? 'text-rose-600'
                          : feature.gradient.includes('emerald')
                          ? 'text-emerald-600'
                          : feature.gradient.includes('violet')
                          ? 'text-violet-600'
                          : 'text-blue-600'
                        return (
                          <Icon className={cn("w-6 h-6 drop-shadow-sm", iconColor)} />
                        );
                      })()}
                    </div>

                    {/* Content */}
                    <div className="flex-1">
                      <h3 className="text-xl font-bold mb-2 text-foreground">
                        {feature.title}
                      </h3>
                      <p className="text-muted-foreground leading-relaxed">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  )
}
